import React from "react";
import {
  EmojiEventsOutlined,
  CheckCircleOutline,
  BoltOutlined,
  AccessTimeRounded,
  SportsSoccerOutlined,
  WorkspacePremiumOutlined,
  MilitaryTechOutlined,
} from "@mui/icons-material";
import { POINTS_EXACT, POINTS_OUTCOME } from "../../utils/betScoring";
import "./BrowserRules.scss";

interface KnockoutRow {
  round: string;
  outcome: number;
  exact: number;
}

// Knockout points scale up each round (mirrors the WC2026 backend TyperScore
// enum and the values listed in the FAQ).
const knockoutRows: KnockoutRow[] = [
  { round: "Round of 32", outcome: 3, exact: 6 },
  { round: "Round of 16", outcome: 4, exact: 8 },
  { round: "Quarter-finals", outcome: 5, exact: 10 },
  { round: "Semi-finals", outcome: 6, exact: 12 },
  { round: "Third-place play-off", outcome: 6, exact: 12 },
  { round: "Final", outcome: 7, exact: 14 },
];

const tips = [
  {
    icon: <AccessTimeRounded />,
    title: "Bet before kick-off",
    text: "Predictions lock the moment a match starts. Miss the deadline and you can't score on that game.",
  },
  {
    icon: <SportsSoccerOutlined />,
    title: "90 minutes only",
    text: "In the knockout stage only the result after regular time (90') counts — extra time and penalties are ignored.",
  },
  {
    icon: <MilitaryTechOutlined />,
    title: "Tie-breaker",
    text: "Level on points? The player with more Perfect Scores ranks higher.",
  },
  {
    icon: <EmojiEventsOutlined />,
    title: "Winning a league",
    text: "Top the points table in a league to win it — every correct prediction counts.",
  },
];

const BrowserRules: React.FC = () => {
  return (
    <div className="browserRules__root">
      <div className="browserRules__inner">
        <header className="browserRules__hero">
          <h1 className="browserRules__title">How scoring works</h1>
          <p className="browserRules__subtitle">
            Predict the final score of every match. The closer your prediction,
            the more points you earn.
          </p>
        </header>

        <section className="browserRules__points">
          <article className="browserRules__pointCard">
            <span className="browserRules__pointIcon">
              <CheckCircleOutline />
            </span>
            <span className="browserRules__pointValue">+{POINTS_OUTCOME}</span>
            <span className="browserRules__pointLabel">Right outcome</span>
            <p className="browserRules__pointDesc">
              You called the winner (or a draw) but not the exact score.
            </p>
          </article>

          <article className="browserRules__pointCard browserRules__pointCard--accent">
            <span className="browserRules__pointIcon">
              <BoltOutlined />
            </span>
            <span className="browserRules__pointValue">+{POINTS_EXACT}</span>
            <span className="browserRules__pointLabel">Perfect score</span>
            <p className="browserRules__pointDesc">
              You nailed the exact final score — the outcome is included.
            </p>
          </article>
        </section>

        <section className="browserRules__section">
          <div className="browserRules__sectionHead">
            <WorkspacePremiumOutlined />
            <h2>Knockout stage bonus</h2>
          </div>
          <p className="browserRules__sectionLead">
            Points climb the deeper the tournament goes — the same prediction is
            worth more in later rounds.
          </p>

          <div className="browserRules__table">
            <div className="browserRules__tableHead">
              <span>Round</span>
              <span>Right outcome</span>
              <span>Perfect score</span>
            </div>
            <div className="browserRules__tableRow">
              <span className="browserRules__round">Group stage</span>
              <span className="browserRules__pts">+{POINTS_OUTCOME}</span>
              <span className="browserRules__pts browserRules__pts--accent">
                +{POINTS_EXACT}
              </span>
            </div>
            {knockoutRows.map((row) => (
              <div className="browserRules__tableRow" key={row.round}>
                <span className="browserRules__round">{row.round}</span>
                <span className="browserRules__pts">+{row.outcome}</span>
                <span className="browserRules__pts browserRules__pts--accent">
                  +{row.exact}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="browserRules__section">
          <div className="browserRules__sectionHead">
            <EmojiEventsOutlined />
            <h2>Good to know</h2>
          </div>
          <div className="browserRules__tips">
            {tips.map((tip) => (
              <article className="browserRules__tip" key={tip.title}>
                <span className="browserRules__tipIcon">{tip.icon}</span>
                <div>
                  <h3 className="browserRules__tipTitle">{tip.title}</h3>
                  <p className="browserRules__tipText">{tip.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default BrowserRules;
